import { Database, DatabaseZap, Plus, Search } from "lucide-react";
import * as React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { DropdownMenu, RobotoButton } from "@/shared/foundation/primitives";

import { FormBlock } from "./FormBlock";
import {
  INPUT_SOURCE_COPY,
  triggeringDatasetCopy,
  type TriggeringSubject,
} from "./inputSourceCopy";
import { InputSourceRow } from "./InputSourceRow";
import styles from "./invocationSpec.module.css";
import { type InputSourceValues } from "./invocationSpecSchema";

/** The sources the menu offers, in the order it shows them. */
const SOURCES: {
  key: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  /** Which firing the entry is worded for, when its wording depends on one. */
  subject?: TriggeringSubject;
  values: () => InputSourceValues;
}[] = [
  {
    key: "dataset",
    icon: <Database />,
    label: INPUT_SOURCE_COPY.dataset.title,
    description: INPUT_SOURCE_COPY.dataset.description,
    values: () => ({ source: "dataset", datasetId: null, globs: [] }),
  },
  {
    key: "query",
    icon: <Search />,
    label: INPUT_SOURCE_COPY.query.title,
    description: INPUT_SOURCE_COPY.query.description,
    values: () => ({ source: "query", text: "" }),
  },
];

/**
 * The dataset the firing carries, offered only when it carries one: the
 * runtime fails a dispatch whose placeholder resolves to nothing, so a
 * subscription with no dataset in its namespace must not offer this row.
 */
function triggeringDatasetSource(subject: TriggeringSubject) {
  const copy = triggeringDatasetCopy(subject);
  return {
    key: "triggering-dataset",
    icon: <DatabaseZap />,
    label: copy.title,
    description: copy.description,
    subject,
    values: (): InputSourceValues => ({
      source: "triggering-dataset",
      globs: [],
    }),
  };
}

export interface InputDataBlockProps {
  /** Where the invocation block lives in the enclosing form. */
  name: string;
  /**
   * What the firing is about, when its namespace carries a dataset. Absent
   * when it carries none, and the row is then not on offer.
   */
  triggeringSubject?: TriggeringSubject;
  /** What the invocation is already handed, shown above the rows. */
  header?: React.ReactNode;
  /** Sources the caller adds to the menu, listed before the block's own. */
  offers?: readonly InputDataOffer[];
}

export interface InputDataOffer {
  key: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  onSelect: () => void;
}

/**
 * What the invocation reads: what it is already handed, then the sources a
 * person adds on top.
 */
export function InputDataBlock({
  name,
  header,
  offers = [],
  triggeringSubject,
}: InputDataBlockProps) {
  const { control } = useFormContext();
  const titleId = React.useId();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${name}.inputs`,
  });

  return (
    <FormBlock
      titleId={titleId}
      title="Input data"
      hint="The files the invocation downloads for the action. Add an input for anything else it needs."
      data-testid="input-data"
    >
      {header}

      {fields.length > 0 && (
        <div data-testid="input-sources" data-count={fields.length}>
          <div className={styles.group}>
            {fields.map((field, index) => (
              <InputSourceRow
                key={field.id}
                name={`${name}.inputs.${index}`}
                index={index}
                triggeringSubject={triggeringSubject}
                onRemove={() => {
                  remove(index);
                }}
              />
            ))}
          </div>
        </div>
      )}

      <DropdownMenu>
        <DropdownMenu.Trigger
          render={
            <RobotoButton
              variant="secondary"
              icon={<Plus aria-hidden size={16} />}
              data-testid="add-input"
            >
              Add input
            </RobotoButton>
          }
        />
        <DropdownMenu.Content
          className={styles.menu}
          data-testid="add-input-menu"
        >
          {[
            ...offers,
            ...[
              ...(triggeringSubject === undefined
                ? []
                : [triggeringDatasetSource(triggeringSubject)]),
              ...SOURCES,
            ].map((source) => ({
              ...source,
              onSelect: () => {
                append(source.values());
              },
            })),
          ].map((offer) => (
            <DropdownMenu.Item
              key={offer.key}
              icon={offer.icon}
              onClick={offer.onSelect}
              className={styles.menuItem}
              data-testid={`add-input-${offer.key}`}
              data-subject={"subject" in offer ? offer.subject : undefined}
            >
              <span className={styles.menuItemText}>
                <span className={styles.menuItemLabel}>{offer.label}</span>
                <span className={styles.menuItemDescription}>
                  {offer.description}
                </span>
              </span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>
    </FormBlock>
  );
}
